import { useState, useEffect } from 'react'
import { X } from 'lucide-react'

/* ─── Colors ─────────────────────────────────────────────── */
const C = {
  teal: '#2C7A7B',
  tealDark: '#005440',
  tealLight: '#E1F5EE',
  coral: '#E07856',
  coralLight: '#FAECE7',
  coralDark: '#993C1D',
  ink: '#1A202C',
  gray: '#4A5568',
  grayLight: '#718096',
  border: '#E2E8F0',
  surface: '#F7FAFC',
  white: '#FFFFFF',
  amber: '#8b5000',
  amberLight: '#FCF5E8',
  green: '#0F6E56',
  greenLight: '#E1F5EE',
}

/* ─── Button ─────────────────────────────────────────────── */
export function Button({ children, onClick, variant = 'primary', size = 'md', disabled, type = 'button', style: extraStyle, ...props }) {
  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    borderRadius: '6px',
    fontWeight: 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    border: 'none',
    transition: 'background 0.15s ease',
    fontSize: size === 'sm' ? '13px' : '14px',
    padding: size === 'sm' ? '6px 12px' : '10px 16px',
  }

  const variants = {
    primary: { background: C.teal, color: C.white },
    secondary: { background: C.white, color: C.gray, border: `1px solid ${C.border}` },
    danger: { background: C.coralLight, color: C.coralDark },
    ghost: { background: 'transparent', color: C.gray },
    coral: { background: C.coral, color: C.white },
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{ ...base, ...variants[variant], ...extraStyle }}
      {...props}
    >
      {children}
    </button>
  )
}

/* ─── Input ──────────────────────────────────────────────── */
export function Input({ label, id, error, style: extraStyle, ...props }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      {label && (
        <label htmlFor={id} style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: C.gray, marginBottom: '6px' }}>
          {label}
        </label>
      )}
      <input
        id={id}
        style={{
          width: '100%',
          padding: '9px 12px',
          border: `1px solid ${error ? C.coral : C.border}`,
          borderRadius: '6px',
          fontSize: '14px',
          background: C.white,
          ...extraStyle,
        }}
        {...props}
      />
      {error && <p style={{ fontSize: '12px', color: C.coralDark, marginTop: '4px' }}>{error}</p>}
    </div>
  )
}

/* ─── Select ─────────────────────────────────────────────── */
export function Select({ label, id, options, error, ...props }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      {label && (
        <label htmlFor={id} style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: C.gray, marginBottom: '6px' }}>
          {label}
        </label>
      )}
      <select
        id={id}
        style={{
          width: '100%',
          padding: '9px 12px',
          border: `1px solid ${error ? C.coral : C.border}`,
          borderRadius: '6px',
          fontSize: '14px',
          background: C.white,
          appearance: 'none',
        }}
        {...props}
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  )
}

/* ─── Textarea ───────────────────────────────────────────── */
export function Textarea({ label, id, error, ...props }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      {label && (
        <label htmlFor={id} style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: C.gray, marginBottom: '6px' }}>
          {label}
        </label>
      )}
      <textarea
        id={id}
        style={{
          width: '100%',
          padding: '9px 12px',
          border: `1px solid ${error ? C.coral : C.border}`,
          borderRadius: '6px',
          fontSize: '14px',
          background: C.white,
          minHeight: '80px',
          resize: 'vertical',
          fontFamily: 'inherit',
        }}
        {...props}
      />
    </div>
  )
}

/* ─── Card ───────────────────────────────────────────────── */
export function Card({ children, style: extraStyle, ...props }) {
  return (
    <div
      style={{
        background: C.white,
        borderRadius: '10px',
        border: `1px solid ${C.border}`,
        padding: '1.25rem',
        ...extraStyle,
      }}
      {...props}
    >
      {children}
    </div>
  )
}

/* ─── Stat Card ──────────────────────────────────────────── */
export function StatCard({ label, value, sub, color = C.teal }) {
  return (
    <Card>
      <div style={{ fontSize: '12px', fontWeight: 500, color: C.grayLight, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div style={{ fontSize: '28px', fontWeight: 600, color, lineHeight: 1.1, marginBottom: '2px' }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: '13px', color: C.grayLight }}>{sub}</div>}
    </Card>
  )
}

/* ─── Badge ──────────────────────────────────────────────── */
export function Badge({ children, variant = 'default' }) {
  const variants = {
    default: { background: C.tealLight, color: C.tealDark },
    coral: { background: C.coralLight, color: C.coralDark },
    amber: { background: C.amberLight, color: C.amber },
    green: { background: C.greenLight, color: C.green },
    gray: { background: '#EDF2F7', color: C.gray },
  }
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 500,
      ...variants[variant],
    }}>
      {children}
    </span>
  )
}

/* ─── Modal ──────────────────────────────────────────────── */
export function Modal({ open, onClose, title, children, width = 480 }) {
  if (!open) return null

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)' }}
        onClick={onClose}
      />
      <div style={{
        position: 'relative',
        background: C.white,
        borderRadius: '12px',
        padding: '1.5rem',
        width: '100%',
        maxWidth: `${width}px`,
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20px', fontWeight: 500, color: C.ink }}>
            {title}
          </h2>
          <button onClick={onClose} style={{ padding: '4px', color: C.grayLight, cursor: 'pointer', background: 'none', border: 'none' }}>
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

/* ─── Empty State ────────────────────────────────────────── */
export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <Card style={{ textAlign: 'center', padding: '3rem 2rem' }}>
      {Icon && <Icon size={40} style={{ color: C.border, marginBottom: '1rem' }} />}
      <h3 style={{ fontSize: '16px', fontWeight: 500, color: C.ink, marginBottom: '6px' }}>{title}</h3>
      <p style={{ fontSize: '14px', color: C.grayLight, marginBottom: '1.25rem', maxWidth: '360px', margin: '0 auto 1.25rem' }}>{description}</p>
      {action}
    </Card>
  )
}

/* ─── Toast ──────────────────────────────────────────────── */
let toastTimeout = null

export function Toast({ message, type = 'success', onClose }) {
  useEffect(() => {
    toastTimeout = setTimeout(onClose, 3500)
    return () => clearTimeout(toastTimeout)
  }, [onClose])

  const bg = type === 'success' ? C.greenLight : type === 'error' ? C.coralLight : C.amberLight
  const color = type === 'success' ? C.green : type === 'error' ? C.coralDark : C.amber

  return (
    <div style={{
      position: 'fixed',
      bottom: '1.5rem',
      right: '1.5rem',
      zIndex: 100,
      padding: '12px 20px',
      background: bg,
      color,
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 500,
      boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    }}>
      {message}
      <button onClick={onClose} style={{ background: 'none', border: 'none', color, cursor: 'pointer', padding: '2px' }}>
        <X size={16} />
      </button>
    </div>
  )
}

/* ─── Page Header ────────────────────────────────────────── */
export function PageHeader({ title, description, action }) {
  return (
    <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', gap: '1rem', flexWrap: 'wrap' }}>
      <div>
        <h1 style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: '28px',
          fontWeight: 500,
          color: C.ink,
          marginBottom: '4px',
        }}>{title}</h1>
        {description && <p style={{ color: C.gray, fontSize: '15px' }}>{description}</p>}
      </div>
      {action}
    </header>
  )
}

/* ─── Tab Bar ────────────────────────────────────────────── */
export function TabBar({ tabs, activeTab, onTabChange }) {
  return (
    <div style={{ display: 'flex', gap: '2px', borderBottom: `1px solid ${C.border}`, marginBottom: '1.5rem' }}>
      {tabs.map(tab => (
        <button
          key={tab.value}
          onClick={() => onTabChange(tab.value)}
          style={{
            padding: '10px 16px',
            fontSize: '14px',
            fontWeight: activeTab === tab.value ? 500 : 400,
            color: activeTab === tab.value ? C.teal : C.grayLight,
            borderBottom: activeTab === tab.value ? `2px solid ${C.teal}` : '2px solid transparent',
            background: 'none',
            border: 'none',
            borderBottomWidth: '2px',
            borderBottomStyle: 'solid',
            borderBottomColor: activeTab === tab.value ? C.teal : 'transparent',
            cursor: 'pointer',
            marginBottom: '-1px',
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}

/* ─── Checkbox Group ─────────────────────────────────────── */
export function CheckboxGroup({ label, options, selected, onChange }) {
  const toggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  return (
    <div style={{ marginBottom: '1rem' }}>
      {label && (
        <div style={{ fontSize: '13px', fontWeight: 500, color: C.gray, marginBottom: '8px' }}>{label}</div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {options.map(opt => (
          <label
            key={opt.value}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 10px',
              borderRadius: '6px',
              fontSize: '13px',
              cursor: 'pointer',
              background: selected.includes(opt.value) ? C.tealLight : C.white,
              border: `1px solid ${selected.includes(opt.value) ? C.teal : C.border}`,
              color: selected.includes(opt.value) ? C.tealDark : C.gray,
              fontWeight: selected.includes(opt.value) ? 500 : 400,
            }}
          >
            <input
              type="checkbox"
              checked={selected.includes(opt.value)}
              onChange={() => toggle(opt.value)}
              style={{ display: 'none' }}
            />
            {opt.label}
          </label>
        ))}
      </div>
    </div>
  )
}

export { C as colors }
